import React, { useEffect, useState } from 'react';
import { funnelAPI, usersAPI } from '../../services/api';
import { Spinner, Button } from '../../components/ui';
import toast from 'react-hot-toast';

const NAVY = '#1B2A6B';
const GOLD = '#C9A84C';
const FRONTEND_URL = process.env.REACT_APP_URL || 'https://main.d3j084kuc04p9k.amplifyapp.com';

const TYPE_STYLE = {
  franchise:     { bg: '#ede9fe', color: '#7c3aed' },
  student:       { bg: '#dbeafe', color: '#1d4ed8' },
  institutional: { bg: '#dcfce7', color: '#15803d' },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: copied ? '#15803d' : '#64748b', fontFamily: 'inherit', padding: '2px 6px' }}>
      {copied ? '✓' : '📋'}
    </button>
  );
}

const PAGE_TYPE_OPTIONS = ['franchise', 'student', 'institutional'];

const EMPTY_FORM = {
  page_type: 'franchise', title: '', slug: '', assigned_to: '',
  utm_source: '', utm_medium: '', utm_campaign: '',
};

export default function FunnelPagesManager() {
  const [pages, setPages] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editPage, setEditPage] = useState(null); // null = create new
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      funnelAPI.getPages(),
      usersAPI.getAll().catch(() => ({ data: { data: [] } })),
    ]).then(([pRes, uRes]) => {
      setPages(pRes.data?.data || []);
      setTeamMembers(uRes.data?.data?.users || uRes.data?.data || []);
    }).catch(() => toast.error('Failed to load pages'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // Auto-suggest slug from title
  const handleTitleChange = (val) => {
    set('title', val);
    if (!editPage) {
      const slug = val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      set('slug', slug);
    }
  };

  const openCreate = () => {
    setEditPage(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (page) => {
    setEditPage(page);
    setForm({
      page_type: page.page_type,
      title: page.title,
      slug: page.slug,
      assigned_to: page.assigned_to?._id || '',
      utm_source: page.utm_source || '',
      utm_medium: page.utm_medium || '',
      utm_campaign: page.utm_campaign || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug) { toast.error('Title and slug are required'); return; }
    setSaving(true);
    try {
      if (editPage) {
        await funnelAPI.updatePage(editPage._id, form);
        toast.success('Page updated');
      } else {
        await funnelAPI.createPage(form);
        toast.success('Page created');
      }
      setShowModal(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async (page) => {
    try {
      await funnelAPI.updatePage(page._id, { is_active: !page.is_active });
      setPages(prev => prev.map(p => p._id === page._id ? { ...p, is_active: !p.is_active } : p));
    } catch {
      toast.error('Failed to update');
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', fontSize: 14, border: '1.5px solid #e2e8f0',
    borderRadius: 8, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4, display: 'block' };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: NAVY, margin: 0 }}>Funnel Page Manager</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Create and manage public landing pages with UTM tracking</p>
        </div>
        <Button variant="primary" size="sm" onClick={openCreate}>+ Create New Page</Button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Page', 'Type', 'URL', 'Assigned To', 'Visits', 'Submissions', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#94a3b8', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pages.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    No funnel pages yet. Create your first page.
                  </td>
                </tr>
              ) : pages.map(p => {
                const ts = TYPE_STYLE[p.page_type] || TYPE_STYLE.student;
                const pageUrl = `${FRONTEND_URL}/${p.slug}`;
                return (
                  <tr key={p._id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{p.title}</div>
                      {p.utm_campaign && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                          📊 {p.utm_source}/{p.utm_campaign}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{ fontSize: 11, padding: '3px 8px', borderRadius: 8, fontWeight: 700, background: ts.bg, color: ts.color }}>
                        {p.page_type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <code style={{ fontSize: 11, color: NAVY, background: '#e0e7ff', padding: '2px 6px', borderRadius: 4, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          /{p.slug}
                        </code>
                        <CopyButton text={pageUrl} />
                        <a href={pageUrl} target="_blank" rel="noopener noreferrer"
                          style={{ fontSize: 12, color: '#2563eb', textDecoration: 'none' }} title="Open page">↗</a>
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 13, color: '#475569' }}>
                      {p.assigned_to?.name || <span style={{ color: '#94a3b8' }}>Unassigned</span>}
                      {p.assigned_to?.sub_role && <span style={{ fontSize: 11, color: '#94a3b8', display: 'block' }}>{p.assigned_to.sub_role}</span>}
                    </td>
                    <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 700, color: NAVY }}>{p.total_visits}</td>
                    <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 700, color: '#15803d' }}>{p.total_submissions}</td>
                    <td style={{ padding: '12px 14px' }}>
                      <button onClick={() => toggleActive(p)}
                        style={{
                          padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
                          fontSize: 11, fontWeight: 700, fontFamily: 'inherit',
                          background: p.is_active ? '#dcfce7' : '#fee2e2',
                          color: p.is_active ? '#15803d' : '#dc2626',
                        }}>
                        {p.is_active ? '● Active' : '○ Inactive'}
                      </button>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <button onClick={() => openEdit(p)}
                        style={{ border: 'none', background: '#f1f5f9', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer', color: '#475569', fontFamily: 'inherit' }}>
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 16, fontWeight: 800, color: NAVY, margin: 0 }}>
                {editPage ? 'Edit Funnel Page' : 'Create New Funnel Page'}
              </h2>
              <button onClick={() => setShowModal(false)}
                style={{ border: 'none', background: '#f1f5f9', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', fontSize: 18, color: '#64748b' }}>×</button>
            </div>

            <div style={{ padding: '20px 22px', display: 'grid', gap: 14 }}>
              {/* Page Type */}
              <div>
                <label style={labelStyle}>Page Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {PAGE_TYPE_OPTIONS.map(t => {
                    const ts = TYPE_STYLE[t];
                    return (
                      <button key={t} type="button" onClick={() => set('page_type', t)}
                        style={{
                          padding: '6px 14px', borderRadius: 20, border: '1.5px solid',
                          fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
                          background: form.page_type === t ? ts.color : ts.bg,
                          color: form.page_type === t ? '#fff' : ts.color,
                          borderColor: ts.color,
                        }}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={labelStyle}>Page Title *</label>
                <input style={inputStyle} placeholder="e.g. Build Your AI Education Business"
                  value={form.title} onChange={e => handleTitleChange(e.target.value)} />
              </div>

              {/* Slug */}
              <div>
                <label style={labelStyle}>URL Slug *</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 13, color: '#94a3b8', fontFamily: 'monospace' }}>{FRONTEND_URL}/</span>
                  <input style={{ ...inputStyle, flex: 1, fontFamily: 'monospace', fontSize: 13 }}
                    placeholder="franchise" value={form.slug}
                    onChange={e => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    disabled={!!editPage} />
                </div>
                {editPage && <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>Slug cannot be changed after creation</p>}
              </div>

              {/* Assigned To */}
              <div>
                <label style={labelStyle}>Assign To</label>
                <select style={inputStyle} value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
                  <option value="">— Unassigned —</option>
                  {teamMembers.map(u => (
                    <option key={u._id} value={u._id}>{u.name} ({u.sub_role || u.role})</option>
                  ))}
                </select>
              </div>

              {/* UTM Section */}
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px' }}>
                <p style={{ fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 10px' }}>📊 UTM Tracking (optional)</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                  {['utm_source', 'utm_medium', 'utm_campaign'].map(f => (
                    <div key={f}>
                      <label style={{ ...labelStyle, fontSize: 10, color: '#94a3b8' }}>{f}</label>
                      <input style={{ ...inputStyle, fontSize: 12 }} placeholder={f.replace('utm_', '')}
                        value={form[f]} onChange={e => set(f, e.target.value)} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div style={{ padding: '14px 22px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editPage ? 'Save Changes' : 'Create Page'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
